import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useSpryAuth as useOidcAuth } from 'spry-apps-dropdown';
import { authAPI, setAccessToken } from '../api/backend';
import {
  getSdkAuth,
  subscribeSdkAuth,
  isSdkMode,
  type SdkAuthState,
} from '../sdk/sdkAuthBridge';

interface User {
  id: string;
  name?: string;
  email?: string;
  avatar?: string;
  sprydriveToken?: string;
}

const CALLBACK_PATH = '/auth/oidc/callback';

const pickNonEmptyString = (...values: Array<unknown>) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
};

const pickEmail = (...values: Array<unknown>) => {
  for (const value of values) {
    if (typeof value === 'string' && value.includes('@') && value.trim()) {
      return value.trim().toLowerCase();
    }
  }
  return undefined;
};

const normalizeUser = (payload: any, oidcProfile?: Record<string, unknown>): User | null => {
  const raw =
    payload?.user ??
    payload?.data?.user ??
    payload?.data ??
    payload;

  const backendName = pickNonEmptyString(
    raw?.name,
    raw?.fullName,
    raw?.displayName,
    raw?.username,
    raw?.preferred_username,
    [raw?.firstName, raw?.lastName].filter(Boolean).join(' ').trim(),
  );

  const oidcName = pickNonEmptyString(
    oidcProfile?.name,
    [oidcProfile?.given_name, oidcProfile?.family_name].filter(Boolean).join(' ').trim(),
    oidcProfile?.preferred_username,
  );

  const email = pickEmail(
    raw?.email,
    raw?.mail,
    raw?.preferred_username,
    oidcProfile?.email,
    oidcProfile?.preferred_username,
  );

  const id = pickNonEmptyString(
    raw?.id,
    raw?.userId,
    oidcProfile?.sub,
  );

  if (!id && !email) {
    return null;
  }

  const name = pickNonEmptyString(backendName, oidcName, email?.split('@')[0]);
  const avatar = pickNonEmptyString(raw?.avatar, raw?.picture, oidcProfile?.picture);

  return {
    id: id || email || 'unknown-user',
    name,
    email,
    avatar,
    sprydriveToken: raw?.sprydriveToken,
  };
};

const sdkAuthSnapshot = (): SdkAuthState | null => getSdkAuth();
const sdkAuthServerSnapshot = (): SdkAuthState | null => null;

const useSdkAuth = () => {
  const sdk = useSyncExternalStore(
    subscribeSdkAuth,
    sdkAuthSnapshot,
    sdkAuthServerSnapshot,
  );

  useEffect(() => {
    setAccessToken(sdk?.token ?? null);
    if (!sdk?.refreshToken) return;
    const handler = async () => {
      try {
        const next = await sdk.refreshToken!();
        if (next) setAccessToken(next);
      } catch {
        /* host owns recovery */
      }
    };
    window.addEventListener('spry:auth-required', handler);
    return () => window.removeEventListener('spry:auth-required', handler);
  }, [sdk]);

  const login = useCallback(async () => {
    // No-op in SDK mode — host owns auth.
  }, []);
  const logout = useCallback(async () => {
    sdk?.onSignOut?.();
  }, [sdk]);

  return {
    user: sdk?.user
      ? {
        id: sdk.user.id || sdk.user.email || 'sdk-user',
        name: sdk.user.name,
        email: sdk.user.email,
        avatar: sdk.user.avatar,
      }
      : null,
    token: sdk?.token ?? null,
    loading: !sdk?.token,
    error: null,
    login,
    register: login,
    logout,
    isAuthenticated: !!sdk?.token,
  };
};

export const useAuth = () => {
  // SDK mode: when the host app injected an access token via setSdkAuth(),
  // bypass OIDC entirely. Pick the branch ONCE per app lifetime (isSdkMode is
  // set before render begins) so hook order stays stable.
  if (isSdkMode()) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useSdkAuth();
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const oidc = useOidcAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const authRecoveryInFlightRef = useRef(false);
  // Preserve the last known-good access token so that transient loading states
  // (e.g. background token-refresh triggered by account-manager on initial seed)
  // don't cause downstream effects to treat the session as unauthenticated.
  const lastValidTokenRef = useRef<string | null>(null);

  const rawAccessToken = oidc.user?.access_token || null;
  // Update the preserved ref whenever we have a real token.
  if (rawAccessToken) {
    lastValidTokenRef.current = rawAccessToken;
  }
  // During a loading/refresh cycle (isLoading=true, rawAccessToken momentarily null),
  // fall back to the last known-good token to keep authToken stable.
  // Only return null when truly signed out: isLoading=false AND no token.
  const accessToken = rawAccessToken ?? (oidc.isLoading ? lastValidTokenRef.current : null);

  const startLogin = useCallback(async () => {
    try {
      setError(null);
      try { window.sessionStorage.removeItem('spry:auth-error'); } catch { /* noop */ }
      try {
        const now = Date.now();
        const existing = window.localStorage.getItem('spry:login-in-progress');
        if (existing) {
          const last = Number(existing);
          if (!Number.isNaN(last) && now - last < 120000) return;
        }
        window.localStorage.setItem('spry:login-in-progress', String(now));
      } catch { /* noop */ }

      if (window.location.pathname !== CALLBACK_PATH) {
        window.sessionStorage.setItem('post_login_redirect', window.location.href);
      }
      await oidc.signinRedirect();
    } catch (_err) {
      setError('Login failed');
    }
  }, [oidc]);

  const logout = useCallback(async () => {
    setUser(null);
    setAccessToken(null);
    try {
      await oidc.signoutRedirect();
    } catch (_err) {
      setError('Logout failed');
    }
  }, [oidc]);

  // Keep access token in sync with the API client
  useEffect(() => {
    setAccessToken(accessToken);
  }, [accessToken]);

  const recoverFromUnauthorized = useCallback(async () => {
    if (authRecoveryInFlightRef.current) return;
    if (window.location.pathname === CALLBACK_PATH) return;

    authRecoveryInFlightRef.current = true;
    try {
      if (oidc.isAuthenticated) {
        try {
          await oidc.signinSilent();
          return;
        } catch {
          // fall back to interactive login
        }
      }
      await startLogin();
    } finally {
      window.setTimeout(() => {
        authRecoveryInFlightRef.current = false;
      }, 1000);
    }
  }, [oidc, startLogin]);

  // Sync user profile from backend when token changes
  useEffect(() => {
    const syncUser = async () => {
      if (oidc.isLoading) {
        setLoading(true);
        return;
      }
      if (!accessToken) {
        setUser(null);
        setLoading(false);
        return;
      }
      const oidcProfile = (oidc.user?.profile || {}) as Record<string, unknown>;
      try {
        const response = await authAPI.getCurrentUser();
        setUser(normalizeUser(response.data, oidcProfile));
        setError(null);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401) {
          setUser(null);
          setError('Session expired. Re-authenticating...');
          void recoverFromUnauthorized();
          return;
        }
        // Keep profile identity from token for non-auth failures.
        setUser(normalizeUser({}, oidcProfile));
        setError('Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };
    void syncUser();
  }, [accessToken, oidc.isLoading, oidc.user?.profile, recoverFromUnauthorized]);

  // Auto-redirect to login if unauthenticated
  useEffect(() => {
    const handleAuthRequired = () => {
      void recoverFromUnauthorized();
    };
    window.addEventListener('spry:auth-required', handleAuthRequired);
    return () => window.removeEventListener('spry:auth-required', handleAuthRequired);
  }, [recoverFromUnauthorized]);

  const login = async () => startLogin();
  const register = async () => startLogin();

  return {
    user,
    token: accessToken,
    loading: loading || oidc.isLoading,
    error: error || (oidc.error ? String(oidc.error) : null),
    login,
    register,
    logout,
    isAuthenticated: oidc.isAuthenticated,
  };
};
